import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Trash2 } from "lucide-react";

const inputCls =
  "w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

export const AiQuickLog = ({ date, onLogged }) => {
  const [text, setText] = useState("");
  const [items, setItems] = useState(null);
  const [meal, setMeal] = useState("breakfast");
  const [busy, setBusy] = useState(false);

  const estimate = async () => {
    if (text.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await api.post("/food/ai-parse", { description: text.trim() });
      setItems(r.data.items);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI estimation failed");
    } finally {
      setBusy(false);
    }
  };

  const logAll = async () => {
    setBusy(true);
    try {
      await api.post("/food/log/batch", {
        entries: items.map((it) => ({ date, meal, source: "ai", brand: null, ...it })),
      });
      toast.success(`Logged ${items.length} item${items.length === 1 ? "" : "s"} to ${meal}`);
      setItems(null);
      setText("");
      onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="obs-card" data-testid="ai-quicklog-card">
      <div className="flex items-center gap-3 mb-3">
        <Sparkles className="h-5 w-5 text-blaze" />
        <div>
          <div className="overline">AI quick-log</div>
          <div className="text-xs text-zinc-400 mt-0.5">Describe what you ate — AI estimates the macros</div>
        </div>
      </div>

      {!items ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="ai-quicklog-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && estimate()}
            placeholder='e.g. "2 eggs, toast with butter and a banana"'
            className={inputCls}
          />
          <button
            data-testid="ai-quicklog-estimate"
            onClick={estimate}
            disabled={busy || text.trim().length < 3}
            className="blaze-btn px-5 py-2 text-xs shrink-0 disabled:opacity-50"
          >
            {busy ? "Estimating…" : "Estimate"}
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="ai-quicklog-preview">
          <div className="divide-y divide-obsidian-600/50">
            {items.map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-2 py-2">
                <div className="min-w-0">
                  <div className="text-sm truncate">{it.name}</div>
                  <div className="text-[11px] text-zinc-500">{it.qty_desc}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="font-data text-xs text-zinc-400">
                    {it.calories} kcal · <span className="text-blaze">{it.protein_g}P</span> {it.carbs_g}C {it.fat_g}F
                  </div>
                  <button
                    onClick={() => setItems((arr) => arr.filter((_, k) => k !== i))}
                    className="text-zinc-500 hover:text-red-400"
                    aria-label="Remove item"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="ai-quicklog-meal" value={meal} onChange={(e) => setMeal(e.target.value)} className={`${inputCls} !w-auto`}>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="snack">Snacks</option>
            </select>
            <button
              data-testid="ai-quicklog-confirm"
              onClick={logAll}
              disabled={busy || items.length === 0}
              className="blaze-btn px-5 py-2 text-xs disabled:opacity-50"
            >
              {busy ? "Logging…" : `Log ${items.length} item${items.length === 1 ? "" : "s"}`}
            </button>
            <button onClick={() => setItems(null)} className="ghost-btn px-4 py-2 text-xs">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiQuickLog;
